import type { ErpConnectionView } from '../../api';
import type { Level } from '../EnvironmentReadiness';
import { relativeWhen } from '../distribution/labels';
import type { LinkedWebsite } from './labels';
import { connectionStatusLabel, connectionStatusLevel, linkedWebsites, severalStores, websiteDeliveryState, websiteLabel } from './labels';
import { storeCopyFor } from './storeCopy';
import { usePublicDomains } from './usePublicDomains';

/**
 * ErpFlow draws the one path stock and prices travel — ERP → Commerce Ops →
 * websites — with the health of each hop, so where a problem sits is
 * visible at a glance. It is on the Overview only (CO-158): the header
 * carries the connection's state on every other view.
 */
export function ErpFlow({ connection }: { connection: ErpConnectionView }) {
  const erpLevel: Level = connectionStatusLevel[connection.status];
  const readLevel: Level = syncLevel(connection);
  const linked = linkedWebsites(connection);
  const withStore = severalStores(linked);
  const domains = usePublicDomains(connection.stores);
  return (
    <ol className="erp-flow" aria-label="Where stock and prices come from and go to" data-testid="erp-flow">
      <li className={`erp-flow__node erp-flow__node--${erpLevel}`}>
        <span className="erp-flow__role">ERP</span>
        <span className="erp-flow__name">{connection.provider_info.display_name || connection.provider}</span>
        <span className="erp-flow__fact">
          {connection.company_identifier}
          {connection.warehouses.length > 0
            ? `, ${connection.warehouses.length} ${connection.warehouses.length === 1 ? 'warehouse' : 'warehouses'}`
            : ''}
        </span>
        <span className="erp-flow__state">
          <span className={`level-dot level-dot--${erpLevel}`} aria-hidden="true" />
          {connectionStatusLabel[connection.status]}
        </span>
      </li>
      <li className={`erp-flow__node erp-flow__node--${readLevel}`}>
        <span className="erp-flow__role">Commerce Ops</span>
        <span className="erp-flow__name">
          {connection.enabled && connection.credential_configured ? `Syncs every ${connection.stock_interval_minutes} min` : 'Schedule off'}
        </span>
        <span className="erp-flow__state">
          <span className={`level-dot level-dot--${readLevel}`} aria-hidden="true" />
          {syncStateLabel(connection)}
        </span>
      </li>
      {linked.length === 0 ? (
        <li className="erp-flow__node erp-flow__node--unknown">
          <span className="erp-flow__role">Stores</span>
          <span className="erp-flow__name">No store connected</span>
        </li>
      ) : (
        linked.map((entry) => (
          <StoreNode key={entry.key} entry={entry} withStore={withStore} domain={domains.domainOf(entry.store, entry.website)} connection={connection} />
        ))
      )}
    </ol>
  );
}

/** The health of the scheduled stock sync. */
export function syncLevel(connection: ErpConnectionView): Level {
  if (connection.stale) return 'critical';
  if (connection.last_stock_sync_status === 'failed') return 'warning';
  return connection.last_stock_sync_success_at ? 'ok' : 'unknown';
}

/** The scheduled stock sync's state in words. */
export function syncStateLabel(connection: ErpConnectionView): string {
  if (connection.stale) return 'Stock is out of date';
  if (connection.last_stock_sync_status === 'failed') {
    return connection.consecutive_sync_failures > 1
      ? `Last sync failed (${connection.consecutive_sync_failures} in a row)`
      : 'Last sync failed';
  }
  if (connection.last_stock_sync_status === 'running') return 'Syncing now';
  return connection.last_stock_sync_success_at ? `Last synced ${relativeWhen(connection.last_stock_sync_success_at)}` : 'Not synced yet';
}

/** StoreNode is one store, or one website of a store, that takes stock from the connection (CO-134). */
function StoreNode({ entry, withStore, domain, connection }: { entry: LinkedWebsite; withStore: boolean; domain: string | null; connection: ErpConnectionView }) {
  const { store, website } = entry;
  const state = websiteDeliveryState(store, website, storeCopyFor(store.platform), connection);
  return (
    <li className={`erp-flow__node erp-flow__node--${state.level}`}>
      <span className="erp-flow__role">{website.code === '' ? 'Store' : 'Website'}</span>
      <span className="erp-flow__name">{websiteLabel(store, website, withStore)}</span>
      <span className="erp-flow__fact">{domain ?? store.environment_name}</span>
      <span className="erp-flow__state">
        <span className={`level-dot level-dot--${state.level}`} aria-hidden="true" />
        {state.label}
      </span>
    </li>
  );
}
